import { Link, useSearchParams } from 'react-router-dom';
import { Button, ErrorPlaceholder } from '@tinct/web-components';
import { useStaticAssetsUrl } from '@tinct/core-utils';
import { ACCOUNT_REFUSALS, asAccountRefusal, asSignInRefusal, SIGN_IN_REFUSALS } from '../shared';

/** Wording for the errors this app raises itself (the authorization server sends its own `error_description`). */
const LOCAL_ERROR_DESCRIPTIONS: Record<string, string> = {
  session_unavailable: 'Your account could not be loaded for this application.',
};

const DEFAULT_DESCRIPTION = 'Sign in with an account that has access to this application.';

/**
 * Terminal page of a refused login: the authorization server answered `/login/callback` with an
 * error (`access_denied` when the account lacks back-office access), or the session could not be
 * set up on a token the server did issue. No automatic redirect, so the login never loops; signing
 * out ends the portal session so another account can log in.
 */
export const AccessDeniedPage = () => {
  const [searchParams] = useSearchParams();
  const assetsUrl = useStaticAssetsUrl();

  const error = searchParams.get('error') ?? '';
  const rawDescription = searchParams.get('error_description');
  // The callback page settles these on the login instead of here, but a refusal can still land on
  // this page (a link opened again, a retry already spent): say it in words rather than show the
  // server's token.
  const refusal = asSignInRefusal(rawDescription);
  const accountRefusal = asAccountRefusal(rawDescription);
  const description =
    (refusal && SIGN_IN_REFUSALS[refusal]) ??
    (accountRefusal && ACCOUNT_REFUSALS[accountRefusal]) ??
    rawDescription ??
    LOCAL_ERROR_DESCRIPTIONS[error] ??
    DEFAULT_DESCRIPTION;

  return (
    <div className="h-screen">
      <ErrorPlaceholder
        image={
          <img
            src={`${assetsUrl}/error-analyze.png`}
            alt=""
            className="w-[280px] object-cover"
          />
        }
        title="You are not authorized to access this application"
        description={description}
        actions={
          <Button asChild>
            <Link to="/logout">Sign out</Link>
          </Button>
        }
      />
    </div>
  );
};

export default AccessDeniedPage;
